import React, { useEffect, useState } from "react";
import HamburgerMenuHeader from "../../Header/DeliveryMenuHeader";
import { useNavigate } from "react-router-dom";
import {
  fetchPendingDeliveries,
  monitorResidentAuth,
  sendDeliverySessionAPI,
} from "../../../services/delivery_api";
import delivery_icon from "../../../assets/images/delivery_icon.png";
import cross from "../../../assets/images/cross.png";
import qr_icon from "../../../assets/images/qr_icon.png";
import DeliveryQRLocPopup from "../../common/DeliveryQRLocPopup";
import noPendingDelivery from "../../../assets/images/no_pending_delivery.png";
import { useTranslation } from "react-i18next";

// ⏰ Convert "13-11-2025 02:26:12 PM" → "Nov 13, 2025, 2:26pm"
function formatDate(inputDate) {
  if (!inputDate) return "N/A";

  // inputDate example: "13-11-2025 02:26:12 PM"
  const [datePart, timePart, meridian] = inputDate.split(" ");

  const [day, month, year] = datePart.split("-").map(Number);
  let [hours, minutes, seconds] = timePart.split(":").map(Number);

  // Convert to 24-hour
  if (meridian === "PM" && hours !== 12) hours += 12;
  if (meridian === "AM" && hours === 12) hours = 0;

  const date = new Date(year, month - 1, day, hours, minutes, seconds);

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  return `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}, ${
    date.getHours() % 12 || 12
  }:${String(date.getMinutes()).padStart(2, "0")}${date.getHours() >= 12 ? "pm" : "am"}`;
}

const PendingDelivery = () => {
  const [pending, setPending] = useState([]);
  const navigate = useNavigate();
  const [session, setSession] = useState(null); // stores monitorResidentAuth response
  const [isQrOpen, setIsQrOpen] = useState(false);
  const [complexDict, setComplexDict] = useState({});
  const [showFilterScreen, setShowFilterScreen] = useState(false);
  const [activeTab, setActiveTab] = useState("Locations");
  const [isLoading, setIsLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const statusOptions = [
    { value: "All", label: t("all") },
    { value: "Pending", label: t("pending") },
    { value: "Completed", label: t("completed") },
  ];

  const [selectedLocation, setSelectedLocation] = useState("All");
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [filteredPending, setFilteredPending] = useState(null);
  const [initialized, setInitialized] = useState(false);

  const [qrData, setQrData] = useState({
    otp: "",
    qrUrl: "",
    building: "",
    location: "",
  });

  const isFilterApplied =
    (selectedLocation && selectedLocation !== "All") ||
    selectedBuilding ||
    (selectedStatus && selectedStatus !== "All");

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("pendingDeliveryFilters"));

    if (saved) {
      setSelectedLocation(saved.selectedLocation ?? "All");
      setSelectedBuilding(saved.selectedBuilding ?? null);
      setSelectedStatus(saved.selectedStatus ?? "All");
    }

    setInitialized(true); // allow saving after load
  }, []);

  // 2️⃣ Save filters ONLY after initialization
  useEffect(() => {
    if (!initialized) return; // prevents overwriting on refresh

    localStorage.setItem(
      "pendingDeliveryFilters",
      JSON.stringify({
        selectedLocation,
        selectedBuilding,
        selectedStatus,
      }),
    );
  }, [initialized, selectedLocation, selectedBuilding, selectedStatus]);

  // ✅ Fetch from API when screen loads
  useEffect(() => {
    const loadPendingDeliveries = async () => {
      setIsLoading(true); // ✅ start loading

      try {
        const response = await fetchPendingDeliveries();

        const deliveryList = Array.isArray(response)
          ? response
          : response?.data || [];
        const dict = {};
        deliveryList.forEach((item) => {
          const location = item.complex_location;
          const name = item.complex_name;

          if (!dict[location]) {
            dict[location] = [];
          }
          if (!dict[location].includes(name)) {
            dict[location].push(name);
          }
        });
        setComplexDict(dict);
        console.log("📘 Current dict:", dict);

        console.log("📦 Pending Deliveries API result:", response);

        // 🧩 Format API data to match UI structure
        const formattedDeliveries = deliveryList.map((item, index) => ({
          id: index,
          file_name: item.file_name,

          pendingCount: item.total_pending_authentications ?? 0,
          completedCount: item.total_completed_authentications ?? 0,
          totalCount: item.total_no_of_deliveries ?? 0,

          building: item.complex_name || "Unknown",
          location: item.complex_location || "-",
          dateTime: item.created_at ? formatDate(item.created_at) : "N/A",
          session_card_status: item.session_card_status,

          // ✅ QR-related fields
          dp_web_session_otp: item.dp_web_session_otp,
          delivery_qr_code_url: item.delivery_qr_code_url,
        }));

        setPending(formattedDeliveries);

        // Apply saved filters
        const saved =
          JSON.parse(localStorage.getItem("pendingDeliveryFilters")) || {};

        // ✅ Apply filters if saved ones exist
        let filteredData = [...formattedDeliveries];

        // Apply Location Filter
        if (saved?.selectedLocation && saved.selectedLocation !== "All") {
          filteredData = filteredData.filter(
            (item) => item.location === saved.selectedLocation,
          );
        }

        // Apply Building Filter
        if (saved?.selectedBuilding && saved.selectedBuilding !== "All") {
          filteredData = filteredData.filter(
            (item) => item.building === saved.selectedBuilding,
          );
        }

        // Apply Status Filter
        if (saved?.selectedStatus && saved.selectedStatus !== "All") {
          if (saved.selectedStatus === "Pending") {
            filteredData = filteredData.filter((item) => item.pendingCount > 0);
          }
          if (saved.selectedStatus === "Completed") {
            filteredData = filteredData.filter(
              (item) => item.completedCount > 0,
            );
          }
        }

        setFilteredPending(filteredData);
      } catch (error) {
        console.error("❌ Failed to load pending deliveries:", error);
        setPending([]);
        setFilteredPending([]);
      } finally {
        setIsLoading(false); // ✅ stop loading
      }
    };

    loadPendingDeliveries();
  }, []);

  const handleSubmit = async (item) => {
    const payload = {
      session_file_name: item.file_name,
      complex_location: item.location,
      complex_name: item.building,
    };

    try {
      const response = await monitorResidentAuth(payload);
      console.log("📦 Monitor Resident Auth Result:", response);

      // ✅ Add web_status: "active" to session data
      const sessionData = {
        ...(response?.data?.session_data || {}),
        web_status: "active",
      };
      console.log("PENDING SESSION DATA ---", sessionData);

      // const sendResult = await sendDeliverySessionAPI(sessionData);

      // ✅ Save to state and localStorage
      setSession(sessionData);

      // Optionally save in localStorage if needed across pages
      localStorage.setItem("deliverySession", JSON.stringify(sessionData)); // ✅ FIXED
      // Navigate after storing session
      localStorage.setItem("currentDeliveryStep", 5);

      console.log(
        "📍 currentDeliveryStep:",
        localStorage.getItem("currentDeliveryStep"),
      );

      navigate("/delivery-dashboard");
    } catch (error) {
      console.error("❌ Error calling monitorResidentAuth:", error);
      alert("Failed to fetch delivery status. Please try again.");
    }
  };

  const handleQrClick = (item) => {
    console.log("item.dp_web_session_otp", item.dp_web_session_otp);
    setQrData({
      otp: item.dp_web_session_otp || "",
      qrUrl: item.delivery_qr_code_url || "",
      building: item.building,
      location: item.location,
    });
    setIsQrOpen(true);
  };
  const otpArray =
    (Array.isArray(qrData.otp) ? qrData.otp[0] : qrData.otp)?.split("") || [];

  return (
    <div className="min-h-screen bg-[#002D6A] flex flex-col">
      {/* Header */}
      <HamburgerMenuHeader title={t("pendingDelivery")} />

      {/* apply filter */}
      {showFilterScreen && (
        <div className="fixed left-0 top-[65px] w-full h-[calc(100%-65px)] bg-white flex flex-col z-50">
          {/* Header */}
          <div className="flex justify-between items-center px-5 py-2 shadow-md">
            <h2 className="text-xl font-semibold">{t("advanceFilters")}</h2>
            <button
              onClick={() => setShowFilterScreen(false)}
              className="px-3 py-2 "
            >
              <img src={cross} alt="X" className="w-8 h-8" />
            </button>
          </div>

          <div className="flex flex-1">
            {/* LEFT TABS */}
            <div className="w-1/3 bg-gray-100 border-r">
              <div
                className={`px-4 py-3 cursor-pointer border-l-4 ${
                  activeTab === "Locations"
                    ? "border-blue-600 bg-white font-medium"
                    : "border-transparent text-gray-600"
                }`}
                onClick={() => setActiveTab("Locations")}
              >
                {t("locations")}
              </div>

              <div
                className={`px-4 py-3 cursor-pointer border-l-4 ${
                  activeTab === "Buildings"
                    ? "border-blue-600 bg-white font-medium"
                    : "border-transparent text-gray-600"
                }`}
                onClick={() => setActiveTab("Buildings")}
              >
                {t("buildings")}
              </div>

              <div
                className={`px-4 py-3 cursor-pointer border-l-4 ${
                  activeTab === "Status"
                    ? "border-blue-600 bg-white font-medium"
                    : "border-transparent text-gray-600"
                }`}
                onClick={() => setActiveTab("Status")}
              >
                {t("status")}
              </div>
            </div>

            {/* RIGHT SIDE */}
            <div className="w-2/3 px-6 py-4 overflow-y-auto">
              {/* Locations */}
              {activeTab === "Locations" && (
                <>
                  {/* ALL Option */}
                  <label className="flex items-center gap-3 py-2 cursor-pointer">
                    <input
                      type="radio"
                      name="location"
                      className="w-5 h-5"
                      onChange={() => {
                        setSelectedLocation("All");
                        setSelectedBuilding(null);
                      }}
                      checked={selectedLocation === "All"}
                    />
                    <span>{t("all")}</span>
                  </label>

                  {/* Dynamic Locations */}
                  {Object.keys(complexDict).map((loc) => (
                    <label
                      key={loc}
                      className="flex items-center gap-3 py-2 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="location"
                        className="w-5 h-5"
                        onChange={() => {
                          setSelectedLocation(loc);
                          setSelectedBuilding(null);
                        }}
                        checked={selectedLocation === loc}
                      />
                      <span>{loc}</span>
                    </label>
                  ))}
                </>
              )}

              {/* Buildings */}
              {activeTab === "Buildings" &&
                (selectedLocation && selectedLocation !== "All" ? (
                  <>
                    {/* ALL Buildings option */}
                    <label className="flex items-center gap-3 py-2 cursor-pointer">
                      <input
                        type="radio"
                        name="building"
                        className="w-5 h-5"
                        onChange={() => setSelectedBuilding("All")}
                        checked={selectedBuilding === "All"}
                      />
                      <span>{t("all")}</span>
                    </label>

                    {/* Specific Buildings */}
                    {complexDict[selectedLocation]?.map((bld) => (
                      <label
                        key={bld}
                        className="flex items-center gap-3 py-2 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="building"
                          className="w-5 h-5"
                          onChange={() => setSelectedBuilding(bld)}
                          checked={selectedBuilding === bld}
                        />
                        <span>{bld}</span>
                      </label>
                    ))}
                  </>
                ) : (
                  <p className="text-gray-500">
                    {t("pleaseSelectALocationFirst")}
                  </p>
                ))}

              {/* Status */}
              {activeTab === "Status" &&
                statusOptions.map((st) => (
                  <label
                    key={st.value}
                    className="flex items-center gap-3 py-2 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="status"
                      className="w-5 h-5"
                      onChange={() => setSelectedStatus(st.value)}
                      checked={selectedStatus === st.value}
                    />
                    <span>{st.label}</span>
                  </label>
                ))}
            </div>
          </div>

          {/* APPLY BUTTON */}
          <div className="px-6 py-4 flex justify-end">
            <button
              className="bg-green-600 w-full py-3 rounded-lg text-white font-semibold max-w-[200px] "
              onClick={() => {
                let filtered = pending;

                // 📍 LOCATION Filter
                if (selectedLocation && selectedLocation !== "All") {
                  filtered = filtered.filter(
                    (item) => item.location === selectedLocation,
                  );
                }

                // 🏢 BUILDING Filter
                if (selectedBuilding && selectedBuilding !== "All") {
                  filtered = filtered.filter(
                    (item) => item.building === selectedBuilding,
                  );
                }

                // 🟢 STATUS Filter
                if (selectedStatus && selectedStatus !== "All") {
                  filtered = filtered.filter(
                    (item) =>
                      (selectedStatus === "Pending" &&
                        item.session_card_status === "pending") ||
                      (selectedStatus === "Completed" &&
                        item.session_card_status !== "pending"),
                  );
                }

                setFilteredPending(filtered);
                setShowFilterScreen(false);
              }}
            >
              {t("applyFilter")}
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="bg-white flex-1 px-4 py-6 overflow-y-auto ">
        <div className="flex justify-end mb-4 max-w-[1350px] mx-auto px-2 gap-3">
          {/* Clear Filter Button — only shows if filters applied */}
          {isFilterApplied && (
            <button
              onClick={() => {
                setSelectedLocation("All");
                setSelectedBuilding(null);
                setSelectedStatus(null);
                setFilteredPending(pending); // show all items again
                // Remove saved filters
                // localStorage.removeItem("pendingDeliveryFilters");
              }}
              className="text-sm font-semibold bg-white text-red-600 px-4 py-2 rounded-xl border border-[#DFDFDF]"
            >
              {t("clearFilter")}
            </button>
          )}

          {/* Apply Filter Button */}
          <button
            onClick={() => setShowFilterScreen(true)}
            className="text-sm font-semibold border border-[#DFDFDF] px-4 py-2 rounded-xl "
          >
            {t("applyFilter")}
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-[1350px] mx-auto">
          {/* ✅ STEP 3: Show loading while API is running */}
          {isLoading ? (
            <div className="col-span-full flex justify-center items-center py-20">
              <p className="text-gray-600 font-semibold text-lg">
                {t("loadingDeliveries")}
              </p>
            </div>
          ) : (
            (filteredPending || pending).map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-2xl p-5 mb-4 shadow-sm border border-gray-200"
              >
                {/* Top Row */}
                <div className="flex justify-between items-center mb-4">
                  {/* 3 / 10 Display */}
                  <div className="text-lg font-semibold">
                    {item.pendingCount > 0 ? (
                      <>
                        <span className="text-red-600">
                          {item.pendingCount}
                        </span>
                        <span className="text-[#2699FF]">
                          {" "}
                          / {item.totalCount}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-[#2699FF]">
                          {item.completedCount}
                        </span>
                        <span className="text-[#2699FF]">
                          {" "}
                          / {item.totalCount}
                        </span>
                      </>
                    )}
                  </div>

                  <div
                    className={`px-4 py-1 rounded-full text-sm font-semibold 
                    ${
                      item.session_card_status === "pending"
                        ? "bg-red-100 text-red-600"
                        : "bg-[#E8FFD7] text-[#14AE5C]"
                    }`}
                  >
                    {item.session_card_status === "pending"
                      ? t("pending")
                      : t("completed")}
                  </div>
                </div>

                {/* Building Info */}
                <div className="text-base mb-6">
                  <div className="mb-2">
                    <span className="text-gray-500 font-medium">
                      {t("buildings")}:
                    </span>{" "}
                    <span className="font-bold text-black">
                      {item.building}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">{t("location")}:</span>{" "}
                    <span className="font-bold text-black">
                      {item.location}
                    </span>
                  </div>
                </div>

                {/* Bottom Row: Button (left) + Date (right) */}
                <div className="flex justify-center gap-10 items-center mt-4">
                  <button
                    onClick={() => handleSubmit(item)}
                    className="bg-[#2196F3] text-white text-base flex items-center justify-center gap-2 font-semibold px-5 py-3 rounded-2xl shadow-md w-40"
                  >
                    <img src={delivery_icon} alt="" className="w-5 h-5" />
                    {t("delivery")}
                  </button>

                  <button
                    onClick={() => handleQrClick(item)}
                    className="bg-[#000000] text-white text-base flex items-center justify-center gap-2 font-semibold px-5 py-3 rounded-2xl shadow-md w-40"
                  >
                    <img src={qr_icon} alt="" className="w-5 h-5" />
                    {t("qr")}
                  </button>
                </div>

                {/* Date */}
                <div className="text-center text-gray-500 text-sm leading-snug font-medium pt-3">
                  {(() => {
                    const raw = item.dateTime || "";
                    const parts = raw
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean);
                    if (!raw || parts.length === 0) {
                      return <span>{raw || "N/A"}</span>;
                    }

                    const time = parts[parts.length - 1];
                    const date =
                      parts.length > 1
                        ? parts
                            .slice(0, parts.length - 1)
                            .join(", ")
                            .replace(/,$/, "")
                        : parts[0];

                    return (
                      <>
                        <span>{date}</span> | <span>{time}</span>
                      </>
                    );
                  })()}
                </div>
              </div>
            ))
          )}
        </div>

        {/* ✅ STEP 4: Show empty message ONLY after loading is finished */}
        {!isLoading && (filteredPending || pending)?.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16">
            <img
              src={noPendingDelivery}
              alt="No pending deliveries"
              className="w-60 h-60 object-contain"
            />
            <p className="text-center text-gray-600 font-medium text-lg mt-4">
              {t("noPendingDeliveriesAtTheMoment")}
            </p>
          </div>
        )}
      </div>
      <DeliveryQRLocPopup
        isOpen={isQrOpen}
        onClose={() => setIsQrOpen(false)}
        location={qrData.location}
        building={qrData.building}
        qrImage={qrData.qrUrl}
        numbers={otpArray}
      />
    </div>
  );
};

export default PendingDelivery;
